import React, { useMemo, useState } from "react";
import { ArrowUpRight, MapPin, MessageCircle } from "lucide-react";
import { PageShell } from "../components/Layout";
import "./Contact.css";

const apartments = [
  "Study Apartment",
  "Story Apartment",
  "Silver Screen Apartment",
  "Odeon Apartment",
  "Disco Flame Apartment",
];

export default function Contact() {
  const [name, setName] = useState("");
  const [apartment, setApartment] = useState("");

  const message = useMemo(() => {
    const guest = name.trim() || "a guest";
    const preferred = apartment || "an apartment";
    return `Hello The Bryan Residence, my name is ${guest}. I would like to make an enquiry about ${preferred}.`;
  }, [name, apartment]);

  const whatsappUrl = `https://wa.me/2349168686868?text=${encodeURIComponent(message)}`;
  const instagramUrl = "https://instagram.com/thebryanresidence";

  return (
    <PageShell>
      <main className="contact container">
        <section className="contact__hero">
          <div className="contact__hero-copy">
            <p className="contact__kicker">
              The Bryan Residence · Lekki Phase 1
            </p>
            <h1>
              Let’s plan your
              <br />
              <em>exceptional stay.</em>
            </h1>
            <p className="contact__lede">
              Speak directly with our reservations team on WhatsApp or
              Instagram. Tell us your preferred apartment and we’ll help with
              availability, rates, special requests and every detail of your
              stay.
            </p>
            <div className="contact__availability">
              <span className="contact__availability-dot" /> Direct, personal
              reservation support
            </div>
          </div>

          <div className="contact__enquiry">
            <div className="contact__enquiry-head">
              <span>01 — Start your enquiry</span>
              <small>No email forms. Just direct conversation.</small>
            </div>
            <label>
              Your name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Enter your full name"
              />
            </label>
            <label>
              Preferred apartment
              <select
                value={apartment}
                onChange={(event) => setApartment(event.target.value)}
              >
                <option value="">Select an apartment</option>
                {apartments.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <div className="contact__enquiry-preview">
              <span>Your message</span>
              <p>{message}</p>
            </div>
            <div className="contact__channel-actions">
              <a
                className="contact__whatsapp"
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle size={18} /> Continue on WhatsApp{" "}
                <ArrowUpRight size={15} />
              </a>
              <a
                className="contact__instagram"
                href={instagramUrl}
                target="_blank"
                rel="noreferrer"
              >
                <span className="contact__instagram-mark">ig</span> Message on
                Instagram <ArrowUpRight size={15} />
              </a>
            </div>
            <small className="contact__private-note">
              Your details stay on your device until you choose a channel.
            </small>
          </div>
          <a
            className="contact__hero-arrow"
            href="#visit"
            aria-label="Jump to location"
          >
            <ArrowUpRight size={18} />
          </a>
        </section>

        <section className="contact__steps">
          {[
            [
              "01",
              "Choose your apartment",
              "Study, Story, Silver Screen, Odeon or Disco Flame.",
            ],
            [
              "02",
              "Message our team",
              "Reach us instantly through WhatsApp or Instagram.",
            ],
            [
              "03",
              "Make it yours",
              "We’ll confirm availability, rates and your special requests.",
            ],
          ].map(([number, title, text]) => (
            <article key={number}>
              <span>{number}</span>
              <h2>{title}</h2>
              <p>{text}</p>
            </article>
          ))}
        </section>

        <section className="contact__visit" id="visit">
          <div>
            <p className="eyebrow">Visit The Bryan Residence</p>
            <h2>
              Luxury living,
              <br />
              <em>right in Lekki.</em>
            </h2>
            <p>
              42 Olanrewaju Ninalowo Crescent, Off Kafayat Abdulrazak Street,
              Lekki Phase 1, Lagos, Nigeria.
            </p>
            <a
              href="https://www.google.com/maps?q=42%20Olanrewaju%20Ninalowo%20Crescent%2C%20Lekki%20Phase%201%2C%20Lagos%2C%20Nigeria"
              target="_blank"
              rel="noreferrer"
            >
              Open in Google Maps <MapPin size={15} />
            </a>
          </div>
          <iframe
            title="The Bryan Residence location"
            src="https://www.google.com/maps?q=42%20Olanrewaju%20Ninalowo%20Crescent%2C%20Lekki%20Phase%201%2C%20Lagos%2C%20Nigeria&output=embed"
            loading="lazy"
          />
        </section>

        <section className="contact__channels">
          <div>
            <p className="eyebrow">Connect with us</p>
            <h2>
              A conversation
              <br />
              <em>away.</em>
            </h2>
          </div>
          <div>
            <a href={whatsappUrl} target="_blank" rel="noreferrer">
              <MessageCircle size={23} />
              <span>
                <b>WhatsApp</b>
                <small>Fastest way to reserve your stay</small>
              </span>
              <ArrowUpRight size={18} />
            </a>
            <a href={instagramUrl} target="_blank" rel="noreferrer">
              <span className="contact__large-instagram">ig</span>
              <span>
                <b>Instagram</b>
                <small>@thebryanresidence</small>
              </span>
              <ArrowUpRight size={18} />
            </a>
          </div>
        </section>
      </main>
    </PageShell>
  );
}
